import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Box,
    Card,
    CircularProgress,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    SvgIcon,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {Add, Edit, EditOutlined, MoreHorizRounded, SortRounded, ViewCompactRounded} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../../../packages/data-grid";
import {VendorMembersDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import Button from "../../../../../../components/base/button/button";
import IconButton from "../../../../../../components/base/button/icon_button";
import UpsertVendorMemberDialog from "../../../../../../components/dialogs/upsert-vendor-member";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Popover} from "../../../../../../components/app-specific/popover";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const dataGridSaveConfig = {
    name: 'VendorAccountInfoMembersDataGrid',
    version: 2,
};

const initDialog = {open: false, member: null};

/**
 * Vendor contact users list with persisted grid/list view mode.
 */
const VendorMembersSection = ({vendorId, loading: loadingVendor, deleting = false}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.members'});
    const params = useParams();
    const resolvedVendorId = vendorId ?? params.vendorId;
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removingIds, setRemovingIds] = useState([]);
    const [dialog, setDialog] = useState(initDialog);
    const [viewMode, setViewMode] = useState('grid');
    const [loadingViewMode, setLoadingViewMode] = useState(true);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canManageAccountInfo = operatorAccessLevels.packages.base.manageVendorAccountInfo;
    const {highestVersion: getCachedViewMode, save: saveCachedViewMode} = useSavedComponentsState({
        name: 'vendor-members-list-view-mode',
        version: '1.0.0',
    });

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            name: VendorMembersDataGridCellKeys.remove,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            pinned: true,
            sortable: false,
            width: 0,
            visibilityToggleable: false,
        },
        {
            name: VendorMembersDataGridCellKeys.edit,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            pinned: true,
            sortable: false,
            width: 0,
            visibilityToggleable: false,
        },
        {
            title: t('firstName'),
            name: VendorMembersDataGridCellKeys.firstName,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('lastName'),
            name: VendorMembersDataGridCellKeys.lastName,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('email'),
            name: VendorMembersDataGridCellKeys.email,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('phone'),
            name: VendorMembersDataGridCellKeys.phone,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('role'),
            name: VendorMembersDataGridCellKeys.role,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
    ]), [t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Loads cached list/grid view preference.
     */
    const getViewMode = useCallback(async () => {
        setLoadingViewMode(true);
        const cachedMode = await getCachedViewMode();
        if (cachedMode?.data) {
            setViewMode(cachedMode.data);
        }
        setLoadingViewMode(false);
    }, [getCachedViewMode]);

    useEffect(() => {
        void getViewMode();
    }, [getViewMode]);

    /**
     * Switches and persists view mode between grid and list.
     */
    const handleViewModeChange = useCallback((_, next) => {
        if (!next || next === viewMode)
            return;
        setViewMode(next);
        void saveCachedViewMode(next);
    }, [saveCachedViewMode, viewMode]);

    /**
     * Fetches vendor users for the scoped vendor.
     */
    const loadMembers = useCallback(async () => {
        if (!resolvedVendorId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await Api.listVendorUsers({
            vendorId: Number(resolvedVendorId),
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const payload = response.data;
            setMembers(Array.isArray(payload) ? payload : (payload?.items ?? []));
        } else {
            setMembers([]);
        }
        setLoading(false);
    }, [isMounted, resolvedVendorId]);

    useEffect(() => {
        void loadMembers();
    }, [loadMembers]);

    /**
     * Soft-deletes a vendor user.
     */
    const removeMember = useCallback(async (member) => {
        setRemovingIds(prev => [...prev, member.id]);
        const response = await Api.deleteVendorUser(member.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setMembers(prev => prev.filter(e => e.id !== member.id));
        }
        setRemovingIds(prev => prev.filter(id => id !== member.id));
    }, [isMounted]);

    const busy = loadingVendor || loading || deleting;

    const memberDisplay = useCallback((member) => ({
        firstName: member.firstName ?? member.firstname ?? '',
        lastName: member.lastName ?? member.lastname ?? '',
        email: member.email ?? '',
        phone: member.phone ?? '',
        role: member.role ?? member.position ?? '',
    }), []);

    const rows = useMemo(() => members.map((member) => {
        const display = memberDisplay(member);
        const removing = removingIds.includes(member.id);
        return {
            key: member.id,
            data: member,
            cells: {
                [VendorMembersDataGridCellKeys.remove]: canManageAccountInfo && (
                    <IconButton
                        color={'error'}
                        disabled={busy || removing}
                        onClick={() => removeMember(member)}
                    >
                        {
                            removing
                                ? <CircularProgress size={16}/>
                                : <SvgIcon component={RemoveIcon} inheritViewBox fontSize="small"/>
                        }
                    </IconButton>
                ),
                [VendorMembersDataGridCellKeys.edit]: canManageAccountInfo && (
                    <IconButton
                        color={'secondary'}
                        disabled={busy || removing}
                        onClick={() => setDialog({open: true, member})}
                    >
                        <Edit fontSize="small"/>
                    </IconButton>
                ),
                [VendorMembersDataGridCellKeys.firstName]: display.firstName,
                [VendorMembersDataGridCellKeys.lastName]: display.lastName,
                [VendorMembersDataGridCellKeys.email]: display.email,
                [VendorMembersDataGridCellKeys.phone]: display.phone,
                [VendorMembersDataGridCellKeys.role]: display.role,
            },
        };
    }), [busy, canManageAccountInfo, memberDisplay, members, removeMember, removingIds]);

    return (
        <Paper sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%'}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        {t('title')}
                    </Typography>
                    <Stack sx={{
                        border: '1px solid transparent',
                        borderColor: 'divider',
                        padding: 0.5,
                        borderRadius: 1,
                        width: 'fit-content',
                    }}>
                        <ToggleButtonGroup
                            disabled={loadingViewMode || deleting}
                            sx={{
                                '& .MuiToggleButtonGroup-grouped': {
                                    borderRadius: '0.5rem',
                                    width: 30,
                                    height: 30,
                                },
                                '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                                    marginLeft: '.25rem',
                                },
                            }}
                            size={'small'}
                            value={viewMode}
                            onChange={handleViewModeChange}
                            exclusive
                        >
                            <ToggleButton
                                value={'list'}
                                sx={{backgroundColor: viewMode === 'list' ? 'divider' : 'transparent', border: 'none'}}
                            >
                                <Tooltip title={t('listView')}>
                                    <SortRounded sx={{padding: 0.25}}/>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton
                                value={'grid'}
                                sx={{backgroundColor: viewMode === 'grid' ? 'divider' : 'transparent', border: 'none'}}
                            >
                                <Tooltip title={t('gridView')}>
                                    <ViewCompactRounded sx={{padding: 0.25}}/>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Box>
                {
                    canManageAccountInfo &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => setDialog({open: true, member: null})}
                        disabled={busy}
                    >
                        <Add className={'icon small'} style={{marginRight: 4}}/>
                        {t('add')}
                    </Button>
                }
            </Box>
            {
                loadingViewMode &&
                <LoadingLottieAnimation width={'80%'} className={'my-5'}/>
            }
            {
                !loadingViewMode && viewMode === 'list' &&
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{columns}}
                    columns={dataGridColumns}
                    rows={rows}
                    hideFooterActions
                    hideExporting
                    state={{
                        loading: {state: loadingVendor || loading},
                        density: dataGridDensity,
                    }}
                />
            }
            {
                !loadingViewMode && viewMode === 'grid' &&
                <Grid2 container spacing={2}>
                    {
                        (loadingVendor || loading) &&
                        Array.from({length: 2}).map((_, index) => (
                            <Grid2 xs={'auto'} key={index}>
                                <Skeleton height={180} sx={{transform: 'none', width: 'min(250px, 90vw)'}}/>
                            </Grid2>
                        ))
                    }
                    {
                        !loadingVendor && !loading && !members.length &&
                        <Grid2 xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    my: 5,
                                    py: 3,
                                }}
                            >
                                <Typography variant={'body2'} color={'text.secondary'} align={'center'}>
                                    {t('empty')}
                                </Typography>
                            </Box>
                        </Grid2>
                    }
                    {
                        !loadingVendor && !loading && members.map((member) => {
                            const display = memberDisplay(member);
                            const removing = removingIds.includes(member.id);
                            return (
                                <Grid2 key={member.id} xs={'auto'}>
                                    <Card
                                        variant={'outlined'}
                                        sx={{
                                            paddingInlineStart: 2,
                                            paddingInlineEnd: 1,
                                            paddingBlock: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            width: 'min(250px, 100%)',
                                        }}
                                    >
                                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                            <Typography variant={'subtitle1'} fontWeight={600}>
                                                {`${display.firstName} ${display.lastName}`.trim()}
                                            </Typography>
                                            {
                                                canManageAccountInfo &&
                                            <Popover
                                                elevation={2}
                                                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                                child={(onEnter) => (
                                                    <IconButton
                                                        disabled={busy || removing}
                                                        color={'paper.secondary'}
                                                        onPressed={onEnter}
                                                    >
                                                        <SvgIcon className={'icon normal'} component={MoreHorizRounded}/>
                                                    </IconButton>
                                                )}
                                                popoverChild={(_, close) => (
                                                    <Box sx={{gap: 0, display: 'flex', flexDirection: 'column', padding: 1}}>
                                                        <MenuItem
                                                            disabled={busy || removing}
                                                            sx={{margin: 0, width: '100%'}}
                                                            onClick={() => {
                                                                setDialog({open: true, member});
                                                                close();
                                                            }}
                                                        >
                                                            <EditOutlined className={'icon normal'} sx={{marginInlineEnd: 1}}/>
                                                            {t('edit')}
                                                        </MenuItem>
                                                        <MenuItem
                                                            disabled={busy || removing}
                                                            sx={{margin: 0, width: '100%', color: 'error.main'}}
                                                            onClick={() => {
                                                                void removeMember(member);
                                                                close();
                                                            }}
                                                        >
                                                            {
                                                                removing
                                                                    ? <CircularProgress size={20} className={'icon normal'}/>
                                                                    : <SvgIcon component={RemoveIcon} className={'icon normal'} sx={{color: 'inherit', marginInlineEnd: 1}}/>
                                                            }
                                                            {t('remove')}
                                                        </MenuItem>
                                                    </Box>
                                                )}
                                            />
                                            }
                                        </Box>
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                            <Typography variant={'body2'} color={'text.secondary'}>
                                                {t('email')}: {display.email}
                                            </Typography>
                                            <Typography variant={'body2'} color={'text.secondary'}>
                                                {t('phone')}: {display.phone}
                                            </Typography>
                                            {
                                                !!display.role &&
                                                <Typography variant={'body2'} color={'text.secondary'}>
                                                    {t('role')}: {display.role}
                                                </Typography>
                                            }
                                        </Box>
                                    </Card>
                                </Grid2>
                            );
                        })
                    }
                </Grid2>
            }
            <UpsertVendorMemberDialog
                open={dialog.open}
                close={() => setDialog(initDialog)}
                member={dialog.member}
                vendorId={Number(resolvedVendorId)}
                onSuccess={() => {
                    setDialog(initDialog);
                    void loadMembers();
                }}
            />
        </Paper>
    );
};

export default VendorMembersSection;
